import React, { memo } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup/dist/yup';
import { startOfTomorrow } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';

import { Datepicker, SubmissionButtons } from '@components/common';

import { endOfDayInUTC, endOfTomorrowInUTC } from '@utils/kpi-utils/date';

import { StopRentingAssetFormValues } from '@interfaces/rent/assetRent';
import { IStopRentingAssetDTO } from '@interfaces/rent/dto/assetRentDTO';

import { EValidationMode } from '@enums/validationMode';

import { timezoneUTC } from '@constants/common';

import { validationSchema } from './schema';

import styles from './StopRentingAssetForm.module.scss';

interface IStopRentingAssetForm {
	closeModal: () => void;
	itemId: string | number;
	handleStopRentingAsset: ({ itemId, from }: IStopRentingAssetDTO) => void;
	actualEndRentDate: number | null;
}

const StopRentingAssetForm = ({
	closeModal,
	itemId,
	handleStopRentingAsset,
	actualEndRentDate,
}: IStopRentingAssetForm) => {
	const {
		clearErrors,
		formState: { errors },
		handleSubmit,
		setValue,
		watch,
	} = useForm<StopRentingAssetFormValues>({
		mode: EValidationMode.onSubmit,
		resolver: yupResolver(validationSchema),
	});

	const setEndRentDate = (date: number | null) => {
		if (date) clearErrors('endRentDate');

		setValue('endRentDate', date);
	};

	const endRentDate = watch('endRentDate');

	const selected = endRentDate ? new Date(endRentDate) : null;

	const onSubmit = (values: StopRentingAssetFormValues) => {
		handleStopRentingAsset({
			itemId,
			from: values?.endRentDate ? endOfDayInUTC(new Date(values.endRentDate)) : endOfTomorrowInUTC(),
		});

		closeModal();
	};

	return (
		<>
			<div>
				Are you sure you want to change the rental period?
			</div>
			<div className={styles.datepickerInput}>
				<Datepicker
					disableInput
					errorMessage={errors?.endRentDate?.message}
					handleChange={setEndRentDate}
					isClearable
					isMobileDevice={false}
					isTodayButton={false}
					label='End rent date'
					maxDate={actualEndRentDate ? utcToZonedTime(actualEndRentDate, timezoneUTC) : null}
					minDate={startOfTomorrow()}
					required
					selected={selected}
				/>
			</div>
			<SubmissionButtons
				disabledPositive={!endRentDate}
				negativeHandler={closeModal}
				negativeLabel='No'
				positiveHandler={handleSubmit(onSubmit)}
				positiveLabel='Yes'
			/>
		</>
	);
};

export default memo(StopRentingAssetForm);
